/**
 * EffectsEditor — v10.6.0 Fase 2
 * Editor visual reutilizable para configurar efectos fisiológicos
 * asociados a un medicamento o acción clínica.
 *
 * Props:
 *   sourceType: 'medication' | 'action'
 *   sourceId:   string  (id del medicamento o acción)
 *   sourceName: string  (nombre para mostrar en textos)
 *   disabled:   bool    (deshabilita edición)
 */
import { useState, useEffect, useCallback } from 'react';
import { physiologicalEffectsAPI } from '../../api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Plus, Trash, Pulse, Spinner, ArrowRight, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

// Variables fisiológicas disponibles (mapeadas a patient_current_state)
const TARGET_VARIABLES = [
  { value: 'heart_rate', label: 'Frecuencia cardíaca (lpm)', unit: 'lpm', numeric: true },
  { value: 'systolic_bp', label: 'Presión sistólica (mmHg)', unit: 'mmHg', numeric: true },
  { value: 'diastolic_bp', label: 'Presión diastólica (mmHg)', unit: 'mmHg', numeric: true },
  { value: 'mean_bp', label: 'Presión arterial media (mmHg)', unit: 'mmHg', numeric: true },
  { value: 'spo2', label: 'Saturación O₂ (%)', unit: '%', numeric: true },
  { value: 'respiratory_rate', label: 'Frecuencia respiratoria (rpm)', unit: 'rpm', numeric: true },
  { value: 'temperature', label: 'Temperatura (°C)', unit: '°C', numeric: true },
  { value: 'consciousness', label: 'Conciencia (GCS)', unit: 'GCS', numeric: true },
  { value: 'pain_score', label: 'Escala de dolor (0-10)', unit: '', numeric: true },
  { value: 'end_tidal_co2', label: 'ETCO₂ (mmHg)', unit: 'mmHg', numeric: true },
  { value: 'ecg.rhythm', label: 'Ritmo ECG', unit: '', numeric: false },
];

const OPERATORS = [
  { value: 'SET', label: 'Establecer (=)', desc: 'Fija el valor exacto' },
  { value: 'ADD', label: 'Sumar (+)', desc: 'Aumenta en N unidades' },
  { value: 'SUBTRACT', label: 'Restar (−)', desc: 'Disminuye en N unidades' },
  { value: 'MULTIPLY', label: 'Multiplicar (×)', desc: 'Multiplica por N' },
  { value: 'PERCENTAGE', label: 'Porcentaje (%)', desc: 'Cambio porcentual' },
  { value: 'TARGET', label: 'Objetivo', desc: 'Transición hacia el valor' },
];

const STACKING_MODES = [
  { value: 'stack', label: 'Acumular (stack)' },
  { value: 'replace', label: 'Reemplazar' },
  { value: 'max', label: 'Máximo' },
  { value: 'min', label: 'Mínimo' },
  { value: 'latest', label: 'Más reciente' },
  { value: 'none', label: 'Ninguno' },
];

const LABELS = [
  { value: 'beneficial', label: 'Beneficioso', color: 'bg-green-100 text-green-700 border-green-300' },
  { value: 'neutral', label: 'Neutral', color: 'bg-slate-100 text-slate-700 border-slate-300' },
  { value: 'adverse', label: 'Adverso', color: 'bg-orange-100 text-orange-700 border-orange-300' },
  { value: 'critical', label: 'Crítico', color: 'bg-red-100 text-red-700 border-red-300' },
];

const RHYTHMS = [
  'sinusal', 'taquicardia_sinusal', 'bradicardia_sinusal',
  'fibrilacion_auricular', 'fibrilacion_ventricular',
  'taquicardia_ventricular', 'asistolia', 'aesp',
  'bloqueo_av_completo', 'flutter_auricular',
];

const defaultEffectDef = {
  name: '',
  description: '',
  target_variable: 'heart_rate',
  operator: 'SET',
  value: 100,
  onset_delay_seconds: 0,
  transition_duration_seconds: 5,
  effect_duration_seconds: null,
  priority: 500,
  stacking_mode: 'stack',
  repeatable: true,
  label: 'neutral',
  active: true,
};

const defaultLinkExtras = {
  dose_min: null,
  dose_max: null,
  route: null,
  order: 0,
};

export default function EffectsEditor({ sourceType, sourceId, sourceName, disabled = false }) {
  const [links, setLinks] = useState([]);
  const [allEffects, setAllEffects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState('create'); // 'create' | 'link'
  const [saving, setSaving] = useState(false);
  const [effectForm, setEffectForm] = useState(defaultEffectDef);
  const [linkExtras, setLinkExtras] = useState(defaultLinkExtras);
  const [selectedExistingId, setSelectedExistingId] = useState('');

  const loadData = useCallback(async () => {
    if (!sourceId) return;
    setLoading(true);
    try {
      const [linksRes, effectsRes] = await Promise.all([
        sourceType === 'medication'
          ? physiologicalEffectsAPI.listMedicationLinks(sourceId)
          : physiologicalEffectsAPI.listActionLinks(sourceId),
        physiologicalEffectsAPI.list(),
      ]);
      const linksList = linksRes.data?.links || [];
      const effectsList = effectsRes.data?.effects || [];
      // enrich links with effect details
      const enriched = linksList.map((l) => ({
        ...l,
        effect: effectsList.find((e) => e.id === l.effect_id) || null,
      }));
      setLinks(enriched);
      setAllEffects(effectsList);
    } catch (err) {
      console.error('Error loading effects:', err);
      toast.error('Error al cargar efectos fisiológicos');
    } finally {
      setLoading(false);
    }
  }, [sourceId, sourceType]);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreateModal = () => {
    setMode('create');
    setEffectForm(defaultEffectDef);
    setLinkExtras(defaultLinkExtras);
    setSelectedExistingId('');
    setShowModal(true);
  };

  const openLinkModal = () => {
    setMode('link');
    setLinkExtras(defaultLinkExtras);
    setSelectedExistingId('');
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let effectId = selectedExistingId;

      if (mode === 'create') {
        if (!effectForm.name.trim()) {
          toast.error('Ingrese un nombre para el efecto');
          setSaving(false);
          return;
        }
        // Convertir value según tipo
        const varDef = TARGET_VARIABLES.find((v) => v.value === effectForm.target_variable);
        const payload = {
          ...effectForm,
          value: varDef?.numeric ? Number(effectForm.value) : effectForm.value,
          effect_duration_seconds:
            effectForm.effect_duration_seconds === '' || effectForm.effect_duration_seconds === null
              ? null
              : Number(effectForm.effect_duration_seconds),
        };
        const res = await physiologicalEffectsAPI.create(payload);
        effectId = res.data.id;
      } else {
        if (!selectedExistingId) {
          toast.error('Seleccione un efecto existente');
          setSaving(false);
          return;
        }
      }

      // Link
      const linkPayload = sourceType === 'medication'
        ? {
            medication_id: sourceId,
            effect_id: effectId,
            route: linkExtras.route || null,
            dose_min: linkExtras.dose_min !== null && linkExtras.dose_min !== '' ? Number(linkExtras.dose_min) : null,
            dose_max: linkExtras.dose_max !== null && linkExtras.dose_max !== '' ? Number(linkExtras.dose_max) : null,
            order: Number(linkExtras.order || 0),
          }
        : {
            action_id: sourceId,
            effect_id: effectId,
            order: Number(linkExtras.order || 0),
          };

      if (sourceType === 'medication') {
        await physiologicalEffectsAPI.linkMedication(linkPayload);
      } else {
        await physiologicalEffectsAPI.linkAction(linkPayload);
      }

      toast.success(mode === 'create' ? 'Efecto creado y asociado' : 'Efecto asociado');
      setShowModal(false);
      loadData();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async (link) => {
    if (!window.confirm(`¿Quitar el efecto "${link.effect?.name || link.effect_id}"?`)) return;
    try {
      if (sourceType === 'medication') {
        await physiologicalEffectsAPI.unlinkMedication(link.id);
      } else {
        await physiologicalEffectsAPI.unlinkAction(link.id);
      }
      toast.success('Efecto quitado');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al quitar');
    }
  };

  const varLabel = (v) => TARGET_VARIABLES.find((x) => x.value === v)?.label || v;
  const varUnit = (v) => TARGET_VARIABLES.find((x) => x.value === v)?.unit || '';
  const opSymbol = (op) => ({
    SET: '=', ADD: '+', SUBTRACT: '−', MULTIPLY: '×', PERCENTAGE: '%', TARGET: '→',
  }[op] || op);
  const labelStyle = (l) => LABELS.find((x) => x.value === l)?.color || 'bg-slate-100 text-slate-700';

  const currentVarDef = TARGET_VARIABLES.find((v) => v.value === effectForm.target_variable);
  const isRhythm = effectForm.target_variable === 'ecg.rhythm';

  if (!sourceId) {
    return (
      <div className="text-xs text-slate-500 italic p-3 bg-slate-50 rounded-lg border border-dashed border-slate-200">
        Guarde primero el {sourceType === 'medication' ? 'medicamento' : 'la acción'} para configurar efectos fisiológicos.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid={`effects-editor-${sourceType}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Pulse size={18} className="text-purple-600" />
          <h4 className="text-sm font-semibold text-slate-800">Efectos fisiológicos</h4>
          <Badge variant="secondary">{links.length}</Badge>
        </div>
        {!disabled && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={openLinkModal}
              className="h-8 text-xs"
              data-testid="btn-link-existing-effect"
            >
              Asociar existente
            </Button>
            <Button
              size="sm"
              onClick={openCreateModal}
              className="h-8 text-xs gap-1 bg-purple-600 hover:bg-purple-700"
              data-testid="btn-create-effect"
            >
              <Plus size={14} />
              Crear efecto
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-4">
          <Spinner size={24} className="animate-spin text-slate-400" />
        </div>
      ) : links.length === 0 ? (
        <div className="text-xs text-slate-500 italic p-3 bg-slate-50 rounded-lg border border-dashed border-slate-200">
          Sin efectos configurados. {sourceName} no modificará las variables fisiológicas del paciente.
        </div>
      ) : (
        <div className="space-y-2">
          {links.map((link) => {
            const eff = link.effect;
            if (!eff) return (
              <Card key={link.id} className="border-red-200 bg-red-50">
                <CardContent className="p-3 text-xs text-red-700 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <WarningCircle size={14} />
                    Efecto huérfano ({link.effect_id})
                  </span>
                  {!disabled && (
                    <Button variant="ghost" size="sm" onClick={() => handleUnlink(link)}>
                      <Trash size={14} />
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
            return (
              <Card key={link.id} className="border-slate-200" data-testid={`effect-link-${link.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-900 truncate">{eff.name}</span>
                        <Badge variant="outline" className={`text-[10px] ${labelStyle(eff.label)}`}>
                          {LABELS.find((l) => l.value === eff.label)?.label || eff.label}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-600 flex-wrap">
                        <Badge variant="secondary" className="font-mono text-[11px]">
                          {varLabel(eff.target_variable).split(' (')[0]}
                        </Badge>
                        <span className="font-mono font-semibold text-purple-700">
                          {opSymbol(eff.operator)} {String(eff.value)} {varUnit(eff.target_variable)}
                        </span>
                        {eff.transition_duration_seconds > 0 && (
                          <span className="text-slate-500">
                            <ArrowRight size={10} className="inline" /> {eff.transition_duration_seconds}s
                          </span>
                        )}
                        {eff.effect_duration_seconds && (
                          <span className="text-slate-500">· dura {eff.effect_duration_seconds}s</span>
                        )}
                        {eff.onset_delay_seconds > 0 && (
                          <span className="text-slate-500">· inicio +{eff.onset_delay_seconds}s</span>
                        )}
                      </div>
                      {sourceType === 'medication' && (link.dose_min || link.dose_max || link.route) && (
                        <div className="mt-1.5 flex items-center gap-1 flex-wrap text-[10px] text-slate-500">
                          {link.route && <Badge variant="outline" className="text-[10px]">Vía: {link.route}</Badge>}
                          {(link.dose_min || link.dose_max) && (
                            <Badge variant="outline" className="text-[10px]">
                              Dosis: {link.dose_min ?? 0} – {link.dose_max ?? '∞'}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                    {!disabled && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnlink(link)}
                        className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                        data-testid={`unlink-effect-${link.id}`}
                      >
                        <Trash size={14} />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ============ MODAL ============ */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pulse size={20} className="text-purple-600" />
              {mode === 'create' ? 'Crear efecto fisiológico' : 'Asociar efecto existente'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2 overflow-y-auto max-h-[65vh] pr-2">
            {mode === 'link' ? (
              <div className="space-y-2">
                <Label>Efecto existente *</Label>
                <Select value={selectedExistingId} onValueChange={setSelectedExistingId}>
                  <SelectTrigger data-testid="select-existing-effect">
                    <SelectValue placeholder="Seleccione un efecto del catálogo" />
                  </SelectTrigger>
                  <SelectContent>
                    {allEffects.length === 0 ? (
                      <div className="text-xs text-slate-500 p-3">Sin efectos en el catálogo. Cree uno nuevo.</div>
                    ) : allEffects.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        <span className="flex items-center gap-2">
                          <span>{e.name}</span>
                          <span className="text-xs text-slate-500 font-mono">
                            ({varLabel(e.target_variable).split(' (')[0]} {opSymbol(e.operator)} {String(e.value)})
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <>
                {/* Nombre + Etiqueta */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2 space-y-1.5">
                    <Label htmlFor="eff-name">Nombre del efecto *</Label>
                    <Input
                      id="eff-name"
                      value={effectForm.name}
                      onChange={(e) => setEffectForm({ ...effectForm, name: e.target.value })}
                      placeholder="Ej: Bradicardia sinusal por adenosina"
                      data-testid="eff-name-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Etiqueta clínica</Label>
                    <Select
                      value={effectForm.label}
                      onValueChange={(v) => setEffectForm({ ...effectForm, label: v })}
                    >
                      <SelectTrigger data-testid="eff-label-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LABELS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Variable + Operador + Valor */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label>Variable objetivo *</Label>
                    <Select
                      value={effectForm.target_variable}
                      onValueChange={(v) => setEffectForm({ ...effectForm, target_variable: v, value: v === 'ecg.rhythm' ? 'sinusal' : 100 })}
                    >
                      <SelectTrigger data-testid="eff-var-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TARGET_VARIABLES.map((v) => (
                          <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Operador</Label>
                    <Select
                      value={effectForm.operator}
                      onValueChange={(v) => setEffectForm({ ...effectForm, operator: v })}
                      disabled={isRhythm}
                    >
                      <SelectTrigger data-testid="eff-op-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {OPERATORS.map((o) => (
                          <SelectItem key={o.value} value={o.value} disabled={isRhythm && o.value !== 'SET'}>
                            <span>{o.label}</span>
                            <span className="text-[10px] text-slate-500 ml-2">{o.desc}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Valor *</Label>
                    {isRhythm ? (
                      <Select
                        value={String(effectForm.value)}
                        onValueChange={(v) => setEffectForm({ ...effectForm, value: v })}
                      >
                        <SelectTrigger data-testid="eff-value-rhythm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {RHYTHMS.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, ' ')}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="relative">
                        <Input
                          type="number"
                          step="0.1"
                          value={effectForm.value}
                          onChange={(e) => setEffectForm({ ...effectForm, value: e.target.value })}
                          data-testid="eff-value-input"
                        />
                        {currentVarDef?.unit && (
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                            {currentVarDef.unit}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Preview */}
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <div className="text-[11px] text-purple-700 font-semibold mb-1">Vista previa</div>
                  <div className="text-sm font-mono text-purple-900">
                    {varLabel(effectForm.target_variable).split(' (')[0]}
                    {' '}
                    <span className="font-bold">{opSymbol(effectForm.operator)}</span>
                    {' '}
                    {String(effectForm.value)} {currentVarDef?.unit || ''}
                    {effectForm.transition_duration_seconds > 0 && (
                      <span className="text-purple-600"> · transición {effectForm.transition_duration_seconds}s</span>
                    )}
                    {effectForm.effect_duration_seconds && (
                      <span className="text-purple-600"> · dura {effectForm.effect_duration_seconds}s</span>
                    )}
                  </div>
                </div>

                {/* Timing */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label>Retraso inicio (s)</Label>
                    <Input
                      type="number" min="0"
                      value={effectForm.onset_delay_seconds}
                      onChange={(e) => setEffectForm({ ...effectForm, onset_delay_seconds: parseInt(e.target.value || 0) })}
                      data-testid="eff-onset-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Transición (s)</Label>
                    <Input
                      type="number" min="0"
                      value={effectForm.transition_duration_seconds}
                      onChange={(e) => setEffectForm({ ...effectForm, transition_duration_seconds: parseInt(e.target.value || 0) })}
                      data-testid="eff-transition-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Duración (s, vacío = permanente)</Label>
                    <Input
                      type="number" min="0"
                      value={effectForm.effect_duration_seconds ?? ''}
                      onChange={(e) => setEffectForm({ ...effectForm, effect_duration_seconds: e.target.value === '' ? null : parseInt(e.target.value) })}
                      placeholder="permanente"
                      data-testid="eff-duration-input"
                    />
                  </div>
                </div>

                {/* Stacking + Prioridad */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Modo de combinación</Label>
                    <Select
                      value={effectForm.stacking_mode}
                      onValueChange={(v) => setEffectForm({ ...effectForm, stacking_mode: v })}
                    >
                      <SelectTrigger data-testid="eff-stacking-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STACKING_MODES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Prioridad (0-1000)</Label>
                    <Input
                      type="number" min="0" max="1000"
                      value={effectForm.priority}
                      onChange={(e) => setEffectForm({ ...effectForm, priority: parseInt(e.target.value || 500) })}
                      data-testid="eff-priority-input"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Descripción (opcional)</Label>
                  <Textarea
                    value={effectForm.description || ''}
                    onChange={(e) => setEffectForm({ ...effectForm, description: e.target.value })}
                    placeholder="Uso clínico, contexto…"
                    rows={2}
                    data-testid="eff-description-input"
                  />
                </div>
              </>
            )}

            {/* Link extras: dosis + orden (solo medicamento) */}
            <div className="pt-3 border-t border-slate-200 space-y-3">
              <div className="text-xs font-semibold text-slate-700">
                Parámetros de asociación con {sourceName || (sourceType === 'medication' ? 'este medicamento' : 'esta acción')}
              </div>
              {sourceType === 'medication' && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Dosis mínima</Label>
                    <Input
                      type="number" step="0.01"
                      value={linkExtras.dose_min ?? ''}
                      onChange={(e) => setLinkExtras({ ...linkExtras, dose_min: e.target.value })}
                      placeholder="cualquiera"
                      data-testid="link-dose-min"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Dosis máxima</Label>
                    <Input
                      type="number" step="0.01"
                      value={linkExtras.dose_max ?? ''}
                      onChange={(e) => setLinkExtras({ ...linkExtras, dose_max: e.target.value })}
                      placeholder="cualquiera"
                      data-testid="link-dose-max"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Vía específica</Label>
                    <Input
                      value={linkExtras.route ?? ''}
                      onChange={(e) => setLinkExtras({ ...linkExtras, route: e.target.value })}
                      placeholder="ej: IV (opcional)"
                      data-testid="link-route"
                    />
                  </div>
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs">Orden de aplicación</Label>
                <Input
                  type="number" min="0"
                  value={linkExtras.order}
                  onChange={(e) => setLinkExtras({ ...linkExtras, order: e.target.value })}
                  data-testid="link-order"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} disabled={saving}>Cancelar</Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="save-effect-btn"
            >
              {saving ? <Spinner size={18} className="animate-spin" /> : (mode === 'create' ? 'Crear y asociar' : 'Asociar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
